import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, UserPlus, ListPlus, Home as HomeIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function FamilyVault() {
  const [h, setH] = useState(null);
  const [memberOpen, setMemberOpen] = useState(false);
  const [member, setMember] = useState({ name: "", email: "", relation: "Family" });
  const [grocery, setGrocery] = useState("");

  const load = async () => {
    const { data } = await api.get("/lifestyle/household");
    setH(data);
  };

  useEffect(() => { load(); }, []);

  const addMember = async () => {
    if (!member.name) return;
    try {
      const { data } = await api.post("/lifestyle/household/members", member);
      setH(data);
      setMember({ name: "", email: "", relation: "Family" });
      setMemberOpen(false);
      toast.success("Member added");
    } catch (e) {
      toast.error("Couldn't add member");
    }
  };

  const removeMember = async (id) => {
    const { data } = await api.delete(`/lifestyle/household/members/${id}`);
    setH(data);
  };

  const addGrocery = async () => {
    if (!grocery) return;
    const { data } = await api.post("/lifestyle/household/grocery-list", { name: grocery });
    setH(data);
    setGrocery("");
  };

  const removeGrocery = async (id) => {
    const { data } = await api.delete(`/lifestyle/household/grocery-list/${id}`);
    setH(data);
  };

  if (!h) return <div className="text-gray-500">Loading…</div>;

  return (
    <div data-testid="page-family">
      <PageHeader
        eyebrow="Family Vault"
        title={h.name}
        subtitle="Shared household for grocery lists, vacation goals, and combined cashback."
        action={
          <Dialog open={memberOpen} onOpenChange={setMemberOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" data-testid="add-member">
                <UserPlus className="w-4 h-4 mr-2" /> Add member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add household member</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="mb-1.5 block">Name</Label>
                  <Input value={member.name} onChange={(e) => setMember({ ...member, name: e.target.value })} data-testid="member-name" />
                </div>
                <div>
                  <Label className="mb-1.5 block">Email (optional)</Label>
                  <Input type="email" value={member.email} onChange={(e) => setMember({ ...member, email: e.target.value })} data-testid="member-email" />
                </div>
                <div>
                  <Label className="mb-1.5 block">Relation</Label>
                  <Input value={member.relation} onChange={(e) => setMember({ ...member, relation: e.target.value })} placeholder="Spouse, Parent, Kid…" />
                </div>
              </div>
              <DialogFooter><Button onClick={addMember} className="bg-emerald-700 hover:bg-emerald-800 text-white">Add</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-gray-200 shadow-none rounded-2xl">
          <CardContent className="p-6">
            <h2 className="font-display font-bold text-lg flex items-center gap-2"><HomeIcon className="w-4 h-4 text-emerald-700" /> Members</h2>
            <div className="mt-4 space-y-2">
              {(h.members || []).map((m) => (
                <div key={m.id} className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-3" data-testid={`member-${m.id}`}>
                  <div>
                    <div className="font-semibold">{m.name}</div>
                    <div className="text-xs text-gray-500">{m.relation} {m.email && `· ${m.email}`}</div>
                  </div>
                  <button onClick={() => removeMember(m.id)} className="p-2 text-gray-400 hover:text-red-600" data-testid={`remove-${m.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {(h.members || []).length === 0 && <div className="text-sm text-gray-500">No members yet. Add your spouse, kids, or family.</div>}
            </div>

            <h2 className="font-display font-bold text-lg flex items-center gap-2 mt-8"><ListPlus className="w-4 h-4 text-amber-700" /> Shared grocery list</h2>
            <div className="mt-3 flex gap-2">
              <Input value={grocery} onChange={(e) => setGrocery(e.target.value)} placeholder="Paper towels, milk, diapers…" onKeyDown={(e) => e.key === "Enter" && addGrocery()} data-testid="grocery-input" />
              <Button onClick={addGrocery} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-grocery">Add</Button>
            </div>
            <div className="mt-3 space-y-2">
              {(h.shared_grocery_list || []).map((it) => (
                <div key={it.id} className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-2.5">
                  <span>{it.name}</span>
                  <button onClick={() => removeGrocery(it.id)} className="p-1.5 text-gray-400 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {(h.shared_grocery_list || []).length === 0 && <div className="text-sm text-gray-500">List is empty.</div>}
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-100 shadow-none rounded-2xl gradient-emerald">
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold mb-2">Family Vacation Goal</div>
            <div className="font-display font-bold text-3xl">${(h.vacation_goal || 0).toLocaleString()}</div>
            <p className="text-sm text-gray-700 mt-2">Cashback automatically contributes toward your shared vacation fund.</p>
            <Button asChild className="mt-4 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full">
              <a href="/app/goals">Set savings goals →</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
